import { Box, Card, CardActionArea, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { PROGRAM_TYPES, type ProgramType } from '@altyor-kronos/shared-types';
import { colors } from '../../../core/theme/theme';
import { PROGRAM_TEMPLATES } from '../programTemplates';

export interface StepProgramProps {
  selected: ProgramType | null;
  onSelect: (program: ProgramType) => void;
}

export default function StepProgram({ selected, onSelect }: StepProgramProps) {
  const { t } = useTranslation();

  // Compteurs dérivés du gabarit, jamais écrits en dur : le panneau ne peut pas
  // mentir sur ce que la création va réellement produire.
  const tpl = selected ? PROGRAM_TEMPLATES[selected] : [];
  const reviews = tpl.filter((m) => m.reviewName).length;
  const fvs = tpl.flatMap((m) => m.deliverables).length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        py: 4,
      }}
    >
      <Typography variant="h3" sx={{ textAlign: 'center' }}>
        {t('projects.wizard.programQuestion', 'Select a program')}
      </Typography>

      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
        {PROGRAM_TYPES.map((prog) => {
          const isSelected = prog === selected;
          return (
            <Grid key={prog} size={{ xs: 12, sm: 6 }}>
              <Card
                sx={{
                  position: 'relative',
                  border: isSelected
                    ? `2px solid ${colors.yellow}`
                    : `1px solid ${colors.lightGray}`,
                  backgroundColor: isSelected ? 'rgba(255, 250, 94, 0.06)' : undefined,
                  '&:hover': {
                    backgroundColor: isSelected
                      ? 'rgba(255, 250, 94, 0.1)'
                      : colors.peach,
                  },
                }}
              >
                <CardActionArea onClick={() => onSelect(prog)}>
                  <CardContent sx={{ py: 2.5, px: 2.5 }}>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: colors.red,
                          fontSize: 20,
                        }}
                      />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t(`projects.program.${prog}`, prog)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        `projects.programDescriptions.${prog}`,
                        getProgramFallbackDescription(prog),
                      )}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {selected && (
        <Card
          data-testid="program-seed-preview"
          sx={{ maxWidth: 600, width: '100%', backgroundColor: colors.peach }}
        >
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t('projects.wizard.programSeedTitle')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}>
              <Chip size="small" label={t('projects.wizard.programSeedMilestones', { count: tpl.length })} />
              <Chip size="small" label={t('projects.wizard.programSeedReviews', { count: reviews })} />
              <Chip size="small" label={t('projects.wizard.programSeedDeliverables', { count: fvs })} />
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 1.5 }}>
              {tpl.map((m) => (
                <Chip key={m.templateId} size="small" variant="outlined" label={m.name} />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('projects.wizard.programSeedAdjustable')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

/**
 * Fallback descriptions when i18n keys are not yet defined.
 * These will be replaced by proper translations in the i18n task.
 */
function getProgramFallbackDescription(program: ProgramType): string {
  const descriptions: Record<ProgramType, string> = {
    LaunchPad: 'Full product development from concept to production',
    ScaleUp: 'Scale production and optimize for volume',
    UnLock: 'Unlock stuck projects and resolve technical blockers',
    DiagDesign: 'Design audit and feasibility assessment',
    DiagEco: 'Eco-design diagnosis and sustainability review',
    DiagAmorcageIndus: 'Industrial ramp-up diagnosis',
    Internal: 'Internal Altyor project',
  };
  return descriptions[program];
}
